import React from "react"
import styled, { css } from "styled-components"
import SkeletonLoader from "../../../../components/SkeletonLoader"
import { IconChapa } from "../../../../components/Icons"

const tableColumns = "minmax(48px, 0.6fr) minmax(0, 2fr) minmax(0, 0.9fr) minmax(72px, 0.8fr)";

const RankingWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 20px auto;
  font-family: "Poppins", sans-serif;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    overflow-x: auto;
  }

  @media (max-width: 768px) {
    margin: 12px auto;
  }
`

const RankingHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.5fr;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #000;
  margin-bottom: 10px;
  padding: 12px 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;

  @media (max-width: 768px) {
    overflow-wrap: anywhere;
    font-size: 15px;
  }

  @media (max-width: 415px) {
    font-size: 13px;
  }
`

const RankingCount = styled.span`
  font-size: 14px;
  font-weight: normal;
  color: #555;

  @media (max-width: 415px) {
    font-size: 11px;
  }
`

const CountrySelect = styled.select`
  min-width: 0;
  padding: 6px 28px 6px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  background-color: #fff;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg fill='black' height='14' viewBox='0 0 24 24' width='14' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 14px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    padding: 4px 22px 4px 6px;
    font-size: 12px;
    background-size: 12px;
  }

  @media (max-width: 415px) {
    padding: 3px 18px 3px 4px;
    font-size: 10px;
    background-size: 10px;
  }
`

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${tableColumns};
  font-weight: 600;
  padding: 8px 0;
  border-bottom: 1px solid #ddd;
  color: #444;
  font-size: 14px;
  column-gap: 12px;
  box-sizing: border-box;

  > span {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  @media (max-width: 1024px) {
    column-gap: 6px;
    padding: 8px 4px;
  }

  @media (max-width: 768px) {
    column-gap: 4px;
    padding: 6px 2px;
    font-size: 12px;
  }

  @media (max-width: 415px) {
    column-gap: 2px;
    padding: 4px 1px;
    font-size: 10px;
  }
`

const RankingRow = styled.div`
  display: grid;
  grid-template-columns: ${tableColumns};
  align-items: center;
  padding: 10px;
  font-size: 14px;
  column-gap: 12px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    column-gap: 6px;
    padding: 8px 4px;
  }

  @media (max-width: 768px) {
    column-gap: 4px;
    padding: 6px 2px;
    font-size: 12px;
  }

  @media (max-width: 415px) {
    column-gap: 2px;
    padding: 4px 1px;
    font-size: 10px;
  }

  ${({ $even }) =>
        $even &&
        css`
      background: #f9f9f9;
    `}

  ${({ $odd }) =>
        $odd &&
        css`
      background: #fff;
    `}

  ${({ $highlight }) =>
        $highlight &&
        css`
      background: #000;
      color: #fff;
      font-weight: 600;
      border-radius: 10px;
    `}
`

const Col = styled.div`
  min-width: 0;
  padding: 4px 6px;
  overflow-wrap: anywhere;
  word-break: normal;

  @media (max-width: 768px) {
    padding: 2px;
  }

  @media (max-width: 415px) {
    padding: 1px;
  }
`

const Name = styled(Col)`
  font-weight: 600;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    white-space: normal;
    overflow: visible;
    text-overflow: initial;
    overflow-wrap: anywhere;
  }

  @media (max-width: 415px) {
    font-size: 10px;
  }
`

const Points = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;

  svg {
    flex: 0 0 auto;
  }

  @media (max-width: 415px) {
    gap: 3px;
  }
`

const ErrorMessage = styled.div`
  padding: 16px;
  margin: 20px 0;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #c33;
  text-align: center;
`;

const EmptyMessage = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: #888;
  font-size: 14px;
  grid-column: 1 / -1;

  @media (max-width: 415px) {
    padding: 24px 8px;
    font-size: 12px;
  }
`;

export default function RankingDesktop({
    data,
    total,
    position,
    countries,
    selectedCountry,
    onCountryChange,
    loading = false,
    error = null,
    hideCountrySelect = false,
}) {
    return (
        <RankingWrapper>
            <RankingHeader>
                <Title>
                    Tu Ranking{" "}
                    {loading ? (
                        <SkeletonLoader active width="80px" height="20px" display="inline-block" margin="0 0 0 5px" />
                    ) : (
                        <RankingCount>
                            ({position}/{total})
                        </RankingCount>
                    )}
                </Title>
                {!hideCountrySelect && (
                    <CountrySelect
                        value={selectedCountry}
                        onChange={(e) => onCountryChange(e.target.value)}
                        disabled={loading}
                    >
                        {countries.map((c) => (
                            <option key={c.value} value={c.value}>
                                {c.label}
                            </option>
                        ))}
                    </CountrySelect>
                )}
            </RankingHeader>

            <TableHeader>
                <span>Posición</span>
                <span>Nombre</span>
                <span>{selectedCountry === "commune" ? "Comuna" : selectedCountry === "region" ? "Región" : "País"}</span>
                <span>Valoración</span>
            </TableHeader>

            {error && (
                <ErrorMessage>
                    ⚠️ {error}
                </ErrorMessage>
            )}

            {loading && (
                <>
                    {[...Array(10)].map((_, idx) => (
                        <RankingRow key={`skeleton-${idx}`} $even={idx % 2 === 0} $odd={idx % 2 !== 0}>
                            <Col>
                                <SkeletonLoader active width="40px" height="16px" />
                            </Col>
                            <Col>
                                <SkeletonLoader active width="120px" height="16px" />
                            </Col>
                            <Col>
                                <SkeletonLoader active width="80px" height="16px" />
                            </Col>
                            <Col>
                                <SkeletonLoader active width="60px" height="16px" />
                            </Col>
                        </RankingRow>
                    ))}
                </>
            )}

            {!loading && !error && data.length === 0 && (
                <EmptyMessage>
                    No hay datos de ranking disponibles
                </EmptyMessage>
            )}

            {!loading && !error && data.map((item, idx) => {
                return (
                    <RankingRow
                        key={item._id || idx}
                        $highlight={item.isCurrent}
                        $even={idx % 2 === 0}
                        $odd={idx % 2 !== 0}
                    >
                        <Col>{item.pos || idx + 1}</Col>
                        <Name>{item.name || "Sin nombre"}</Name>
                        <Col>{selectedCountry === "commune" ? (item.city || "-") : selectedCountry === "region" ? (item.region || "-") : (item.country || "-")}</Col>
                        <Col>
                            <Points>
                                <IconChapa width="20" height="20" />
                                {item.points || 0}
                            </Points>
                        </Col>
                    </RankingRow>
                )
            })}
        </RankingWrapper>
    )
}
